import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import * as api from '../services/api';
import * as XLSX from 'xlsx';
import { generatePalleLabel, generateCMR } from '../services/pdf';
import Header from '../components/Header';
import BookingComments from '../components/BookingComments';
import BookingDetail from '../components/BookingDetail';
import './Orders.css';

function OrderRowMenu({ order, navigate, isViewer, onComment }) {
  const { t } = useLanguage();

  const handle = (val) => {
    switch (val) {
      case 'edit': navigate(`/book?edit=${order.id}`); break;
      case 'copy': navigate(`/book?copy=${order.id}`); break;
      case 'comment': onComment(); break;
      case 'palle': generatePalleLabel(order).save(`pallelabel-${order.reference || order.id}.pdf`); break;
      case 'cmr': generateCMR(order).save(`fragtbrev-${order.reference || order.id}.pdf`); break;
      default: break;
    }
  };

  return (
    <span className="row-action-wrap">
      <span className="row-action-label">{t('actions')} ▾</span>
      <select
        className="row-action-select"
        value=""
        onChange={(e) => { if (e.target.value) { handle(e.target.value); e.target.value = ''; } }}
      >
        <option value="">Vælg...</option>
        {!isViewer && !order.locked && <option value="edit">{order.approval_status === 'APPROVED' ? 'Anmod ændring' : t('edit')}</option>}
        {!isViewer && <option value="copy">Kopier</option>}
        <option value="comment">{t('comment')}</option>
        <option value="palle">{t('palleLabel')}</option>
        <option value="cmr">{t('freightLetter')}</option>
      </select>
    </span>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [commentBookingId, setCommentBookingId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    date: '',
    reference: '',
    from: '',
    to: '',
    goods: '',
    temp: '',
    status: '',
  });
  const { token, user } = useAuth();
  const isViewer = user?.role === 'viewer';
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchOrders();
  }, [token]);

  const fetchOrders = async () => {
    try {
      const data = await api.getAllBookings();
      const bookings = data.bookings || data;
      setOrders(
        (Array.isArray(bookings) ? bookings : []).map((b) => ({
          ...b,
          reference: b.booking_reference || `#${b.booking_id}`,
          sender_name: b.pickup_name,
          recipient_name: b.delivery_name,
        }))
      );
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  };

  const getStatusLabel = (order) => {
    const approval = order.approval_status || order.approvalStatus;
    if (approval === 'PENDING_APPROVAL') return t('bookingPending');
    if (approval === 'APPROVED') return t('bookingApproved');
    if (approval === 'REJECTED') return t('bookingRejected');
    if (order.status) return order.status;
    return t('bookingAccepted');
  };

  const getStatusClass = (order) => {
    const approval = order.approval_status || order.approvalStatus;
    if (approval === 'PENDING_APPROVAL') return 'status-badge status-pending';
    if (approval === 'REJECTED') return 'status-badge status-rejected';
    return 'status-badge';
  };

  const getQuantityDisplay = (order) => {
    if (order.lines && order.lines.length > 0) {
      const total = order.lines.reduce((sum, l) => sum + (l.quantity || 1), 0);
      const unit = order.lines[0]?.unit || order.unit || 'Læs';
      return `${total} ${unit}`;
    }
    return `${order.quantity || 1} ${order.unit || 'Læs'}`;
  };

  const getLinesDisplay = (order) => {
    if (order.lines && order.lines.length > 0) {
      return order.lines.map(l => {
        const desc = l.goods_description || `Linje ${l.line_number}`;
        const qty = `${l.quantity || 1} ${l.unit || 'Læs'}`;
        return `${desc} (${qty})`;
      }).join(', ');
    }
    return order.goods_description || '';
  };

  const getLinesCount = (order) => {
    if (order.lines && order.lines.length > 0) return order.lines.length;
    return order.goods_description ? 1 : 0;
  };

  const getTemperatureDisplay = (order) => {
    if (order.lines && order.lines.length > 0) {
      const temps = order.lines
        .filter(l => l.temperature_requirement)
        .map(l => l.temperature_requirement);
      if (temps.length > 0) return [...new Set(temps)].join(', ');
    }
    return order.temperature_requirement || order.temperatureRequirement || '';
  };

  const hasTimeDelivery = (order) => {
    if (order.lines && order.lines.length > 0) {
      return order.lines.some(l => l.time_delivery);
    }
    return false;
  };

  const statusOptions = useMemo(() => {
    const labels = new Set(orders.map(o => getStatusLabel(o)));
    return Array.from(labels);
  }, [orders]);

  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesSearch =
          order.reference?.toLowerCase().includes(search) ||
          order.sender_name?.toLowerCase().includes(search) ||
          order.recipient_name?.toLowerCase().includes(search) ||
          order.booking_reference?.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }

      if (filters.date) {
        const dateStr = `${formatDate(order.pickup_date)} ${formatTime(order.pickup_time)}`;
        if (!dateStr.toLowerCase().includes(filters.date.toLowerCase())) return false;
      }
      if (filters.reference) {
        if (!order.reference?.toLowerCase().includes(filters.reference.toLowerCase())) return false;
      }
      if (filters.from) {
        if (!order.sender_name?.toLowerCase().includes(filters.from.toLowerCase())) return false;
      }
      if (filters.to) {
        if (!order.recipient_name?.toLowerCase().includes(filters.to.toLowerCase())) return false;
      }
      if (filters.goods) {
        const g = filters.goods.toLowerCase();
        const lines = order.lines && order.lines.length > 0 ? order.lines : [{ goods_description: order.goods_description || '' }];
        if (!lines.some(l => (l.goods_description || '').toLowerCase().includes(g))) return false;
      }
      if (filters.temp) {
        const te = filters.temp.toLowerCase();
        const lines = order.lines && order.lines.length > 0 ? order.lines : [{ temperature_requirement: order.temperature_requirement || order.temperatureRequirement || '' }];
        if (!lines.some(l => (l.temperature_requirement || '').toLowerCase().includes(te))) return false;
      }
      if (filters.status) {
        if (getStatusLabel(order) !== filters.status) return false;
      }

      return true;
    });
  }, [orders, searchTerm, filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({ date: '', reference: '', from: '', to: '', goods: '', temp: '', status: '' });
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  const toggleSelect = (orderId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allFilteredIds = new Set(filteredOrders.map(o => o.id));
    if (selectedIds.size === allFilteredIds.size && [...allFilteredIds].every(id => selectedIds.has(id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(allFilteredIds);
    }
  };

  const bulkPrintPallelabels = () => {
    const selected = filteredOrders.filter(o => selectedIds.has(o.id));
    if (selected.length === 0) return;
    const doc = generatePalleLabel(selected[0]);
    for (let i = 1; i < selected.length; i++) {
      const pages = generatePalleLabel(selected[i]);
      const pageCount = pages.internal.getNumberOfPages();
      for (let p = 1; p <= pageCount; p++) {
        doc.addPage([148, 105], 'landscape');
        // Copy doesn't work directly with jsPDF, generate separate files instead
      }
    }
    // Download each as separate file — simpler approach
    selected.forEach(o => {
      generatePalleLabel(o).save(`pallelabel-${o.reference || o.id}.pdf`);
    });
  };

  const bulkPrintCMR = () => {
    const selected = filteredOrders.filter(o => selectedIds.has(o.id));
    selected.forEach(o => {
      generateCMR(o).save(`fragtbrev-${o.reference || o.id}.pdf`);
    });
  };

  const flattenedRows = useMemo(() => {
    const rows = [];
    filteredOrders.forEach(order => {
      const lines = (order.lines && order.lines.length > 0)
        ? order.lines
        : [{ goods_description: order.goods_description || '', quantity: order.quantity || 1, unit: order.unit || 'Læs', temperature_requirement: order.temperature_requirement || order.temperatureRequirement || '', time_delivery: false }];
      lines.forEach((line, i) => {
        rows.push({ order, line, lineIndex: i, lineCount: lines.length });
      });
    });
    return rows;
  }, [filteredOrders]);

  const exportToExcel = () => {
    const data = flattenedRows.map(({ order, line }) => ({
      [t('orderDate')]: `${formatDate(order.pickup_date)} ${formatTime(order.pickup_time)}`,
      [t('orderReference')]: order.reference,
      [t('orderFrom')]: order.sender_name,
      [t('orderTo')]: order.recipient_name,
      'Varebeskrivelse': line.goods_description || '',
      'Antal': line.quantity || 1,
      'Enhed': line.unit || 'Læs',
      'Temperatur': line.temperature_requirement || '',
      'Tidslevering': line.time_delivery ? 'Ja' : '',
      [t('orderStatus')]: getStatusLabel(order),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ordrer');
    XLSX.writeFile(wb, `ordrer_eksport_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div className="dashboard">
      <Header />

      <div className="dashboard-nav">
        <div className="nav-tabs">
          <button
            className={`nav-tab ${location.pathname === '/' ? 'active' : ''}`}
            onClick={() => navigate('/')}
          >
            {t('overview')}
          </button>
          <button
            className={`nav-tab ${location.pathname === '/orders' ? 'active' : ''}`}
            onClick={() => navigate('/orders')}
          >
            {t('orders')}
          </button>
          <button
            className={`nav-tab ${location.pathname === '/calendar' ? 'active' : ''}`}
            onClick={() => navigate('/calendar')}
          >
            {t('calendar')}
          </button>
          <button
            className={`nav-tab ${location.pathname === '/insights' ? 'active' : ''}`}
            onClick={() => navigate('/insights')}
          >
            {t('insights')}
          </button>
          <button
            className={`nav-tab ${location.pathname === '/administration' ? 'active' : ''}`}
            onClick={() => navigate('/administration')}
          >
            {t('administration')}
          </button>
        </div>

        <div className="nav-right">
          <div className="search-box">
            <input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="search-icon">🔍</span>
          </div>

          {!isViewer && (
            <button className="btn-book-order" onClick={() => navigate('/book')}>
              {t('bookOrder')}
            </button>
          )}
        </div>
      </div>

      <div className="orders-page-content">
        <div className="orders-table-container">
          <div className="orders-title-bar">
            <h2 className="page-title">{t('allOrders')}</h2>
            <div className="orders-toolbar">
              {hasActiveFilters && (
                <button className="btn-clear-filters" onClick={clearFilters}>
                  Ryd filtre
                </button>
              )}
              {selectedIds.size > 0 && (
                <>
                  <span className="bulk-count">{selectedIds.size} valgt</span>
                  <button className="btn-bulk" onClick={bulkPrintPallelabels}>{t('printPalleLabels')}</button>
                  <button className="btn-bulk" onClick={bulkPrintCMR}>{t('printFreightLetters')}</button>
                </>
              )}
              <span className="orders-count">{filteredOrders.length} ordre{filteredOrders.length !== 1 ? 'r' : ''}</span>
              <button className="btn-export" onClick={exportToExcel}>
                Eksporter Excel
              </button>
            </div>
          </div>

          {loading ? (
            <p className="loading-text">{t('loading')}</p>
          ) : filteredOrders.length === 0 ? (
            <p className="empty-text">{t('noActiveOrders')}</p>
          ) : (
            <div className="orders-table-scroll">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th className="check-col"><input type="checkbox" onChange={toggleSelectAll} checked={selectedIds.size > 0 && selectedIds.size === filteredOrders.length} /></th>
                    <th>{t('orderDate')}</th>
                    <th>{t('orderReference')}</th>
                    <th>{t('orderFrom')}</th>
                    <th>{t('orderTo')}</th>
                    <th>{t('goodsDescription')}</th>
                    <th>{t('quantity')}</th>
                    <th>{t('unitLabel')}</th>
                    <th>{t('temperature')}</th>
                    <th>{t('orderStatus')}</th>
                    <th>{t('actions')}</th>
                  </tr>
                  <tr className="filter-row">
                    <th></th>
                    <th>
                      <input type="text" className="filter-input" placeholder={t("filter")}
                        value={filters.date} onChange={(e) => handleFilterChange('date', e.target.value)} />
                    </th>
                    <th>
                      <input type="text" className="filter-input" placeholder={t("filter")}
                        value={filters.reference} onChange={(e) => handleFilterChange('reference', e.target.value)} />
                    </th>
                    <th>
                      <input type="text" className="filter-input" placeholder={t("filter")}
                        value={filters.from} onChange={(e) => handleFilterChange('from', e.target.value)} />
                    </th>
                    <th>
                      <input type="text" className="filter-input" placeholder={t("filter")}
                        value={filters.to} onChange={(e) => handleFilterChange('to', e.target.value)} />
                    </th>
                    <th>
                      <input type="text" className="filter-input" placeholder={t("filter")}
                        value={filters.goods} onChange={(e) => handleFilterChange('goods', e.target.value)} />
                    </th>
                    <th></th>
                    <th></th>
                    <th>
                      <input type="text" className="filter-input" placeholder={t("filter")}
                        value={filters.temp} onChange={(e) => handleFilterChange('temp', e.target.value)} />
                    </th>
                    <th>
                      <select className="filter-select" value={filters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}>
                        <option value="">{t('allStatuses')}</option>
                        {statusOptions.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {flattenedRows.map(({ order, line, lineIndex }, idx) => (
                    <tr key={`${order.id}-${lineIndex}`} className="clickable-row" onClick={() => setSelectedOrder(order)}>
                      <td className="check-col" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selectedIds.has(order.id)} onChange={() => toggleSelect(order.id)} /></td>
                      <td>{formatDate(order.pickup_date)} {formatTime(order.pickup_time)}</td>
                      <td className="ref-cell">{order.reference}</td>
                      <td>{order.sender_name}</td>
                      <td>{order.recipient_name}</td>
                      <td className="lines-cell">{line.goods_description || ''}</td>
                      <td className="qty-cell">{line.quantity || 1}</td>
                      <td>{line.unit || 'Læs'}</td>
                      <td className="temp-cell">
                        {line.temperature_requirement && <span className="temp-badge">{line.temperature_requirement}</span>}
                        {line.time_delivery && <span className="time-badge">Tidslevering</span>}
                      </td>
                      <td>
                        <span className={getStatusClass(order)} title={order.rejection_reason || ''}>{getStatusLabel(order)}</span>
                        {order.rejection_reason && <div className="rejection-reason">{order.rejection_reason}</div>}
                      </td>
                      <td className="action-cell" onClick={(e) => e.stopPropagation()}>
                        <OrderRowMenu order={order} navigate={navigate} isViewer={isViewer} onComment={() => setCommentBookingId(order.id)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {selectedOrder && <BookingDetail order={selectedOrder} onClose={() => setSelectedOrder(null)} />}
      {commentBookingId && (
        <BookingComments bookingId={commentBookingId} onClose={() => setCommentBookingId(null)} />
      )}
    </div>
  );
}

export default Orders;
